import React, { useState } from "react";
import { Menu, MenuItem, Stack } from "@mui/material";
import { Visibility, Edit, Delete, RestoreFromTrash, Archive, ContentCopy } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import ModelDelete from "../Models/ModelDelete";

const TableMenu = ({
    anchorEl,
    open,
    onClose,
    onView,
    onRestore,
    onEdit,
    onDelete,
    onDuplicate,
    isDeletingSingle,
    isArchiveMode = false, // ✨ true = we're in the archived items view

    disableRestore = false,
    disableEdit = false,
    disableDelete = false,
    disableDuplicate = false,
}) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";

    const [confirmOpen, setConfirmOpen] = useState(false);

    if (!anchorEl) return null;

    const handleOpenConfirm = () => {
        if (!onDelete || disableDelete || isDeletingSingle) return;
        setConfirmOpen(true);
    };

    const handleCloseConfirm = () => {
        if (isDeletingSingle) return;
        setConfirmOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!onDelete || disableDelete) return;
        await onDelete();
        setConfirmOpen(false);
        onClose?.();
    };

    const handleRestore = () => {
        if (!onRestore || disableRestore) return;
        onRestore();
        onClose?.();
    };

    const handleDuplicate = () => {
        if (!onDuplicate || disableDuplicate) return;
        onDuplicate();
        onClose?.();
    };

    const handleEdit = () => {
        if (!onEdit || disableEdit) return;
        onEdit();
        onClose?.();
    };

    const handleView = () => {
        if (!onView) return;
        onView();
        onClose?.();
    };

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={onClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: isArabic ? "left" : "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: isArabic ? "left" : "right",
                }}
                MenuListProps={{
                    "aria-label": isArabic ? "قائمة الإجراءات" : "actions menu",
                }}
            >
                {onView && (
                    <MenuItem onClick={handleView}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Visibility fontSize="small" color="primary" />
                            <span>{isArabic ? "عرض" : "View"}</span>
                        </Stack>
                    </MenuItem>
                )}

                {onEdit && (
                    <MenuItem
                        onClick={handleEdit}
                        disabled={isDeletingSingle || disableEdit}
                        title={
                            disableEdit
                                ? isArabic
                                    ? "لا تملك صلاحية التعديل"
                                    : "No edit permission"
                                : ""
                        }
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Edit fontSize="small" color="warning" />
                            <span>{isArabic ? "تعديل" : "Edit"}</span>
                        </Stack>
                    </MenuItem>
                )}

                {onDuplicate && !isArchiveMode && (
                    <MenuItem
                        onClick={handleDuplicate}
                        disabled={disableDuplicate}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <ContentCopy fontSize="small" color="info" />
                            <span>{isArabic ? "نسخ" : "Duplicate"}</span>
                        </Stack>
                    </MenuItem>
                )}

                {onRestore && (
                    <MenuItem
                        onClick={handleRestore}
                        disabled={isDeletingSingle || disableRestore}
                        title={
                            disableRestore
                                ? isArabic
                                    ? "لا تملك صلاحية الاستعادة"
                                    : "No restore permission"
                                : ""
                        }
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <RestoreFromTrash fontSize="small" color="success" />
                            <span>{isArabic ? "استعادة" : "Restore"}</span>
                        </Stack>
                    </MenuItem>
                )}

                {onDelete && (
                    <MenuItem
                        onClick={handleOpenConfirm}
                        disabled={isDeletingSingle || disableDelete}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            {isArchiveMode ? (
                                <Delete fontSize="small" color="error" />
                            ) : (
                                <Archive fontSize="small" color="warning" />
                            )}
                            <span>
                                {isArchiveMode 
                                    ? (isArabic ? "حذف نهائي" : "Delete Permanently") 
                                    : (isArabic ? "أرشفة" : "Archive")
                                }
                            </span>
                        </Stack>
                    </MenuItem>
                )}
            </Menu>

            <ModelDelete
                confirmOpen={confirmOpen}
                handleCloseConfirm={handleCloseConfirm}
                isArabic={isArabic}
                isDeletingSingle={isDeletingSingle}
                handleConfirmDelete={handleConfirmDelete}
            />
        </>
    );
};

export default TableMenu;
